import React from 'react';
import { Users, Monitor, ShieldCheck, CheckSquare, TrendingUp, ArrowRight } from 'lucide-react';
import { Language } from '../types';

interface TrainingCatalogProps {
  currentLang: Language;
}

export const TrainingCatalog: React.FC<TrainingCatalogProps> = ({ currentLang }) => {
  return (
    <section id="formations" className="py-16 bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="w-[90%] lg:w-[70%] max-w-none mx-auto px-6">
        
        {/* Header */}
        <div className="mb-12">
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-4">
            {currentLang === 'ar' 
              ? 'برامج التدريب والتأهيل الاحترافي' 
              : currentLang === 'en' 
              ? 'Elevate Your Corporate Capabilities' 
              : 'Programmes de Formation & Qualification'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {currentLang === 'ar'
              ? 'طوّر كفاءات ومهارات فرق عملك عبر تدريبات نخبوية متخصصة في التدقيق، معايير الجودة ISO، الأمن والسلامة والأداء التشغيلي. متوفرة حضورياً وعن بُعد.'
              : currentLang === 'en' 
              ? 'Master modern IT frameworks, audit methodologies, and strategic management through our bespoke training modules designed for high-performance teams.' 
              : 'Développez les compétences de vos équipes avec nos formations spécialisées en Audit, Qualité et Performance. Disponibles en présentiel et en ligne.'}
          </p>
        </div>

        {/* Modalités */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
             {currentLang === 'ar' ? 'صيغ وطرق التدريب' : currentLang === 'en' ? 'Learning Modalities' : "Modalités d'apprentissage"}
          </h3>
          <div className="flex flex-col gap-4">
            <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex gap-4 rounded-sm">
               <div className="mt-1">
                 <Users className="w-5 h-5 text-slate-700 dark:text-slate-300" />
               </div>
               <div>
                 <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                   {currentLang === 'ar' ? 'تدريب حضوري' : currentLang === 'en' ? 'On-site / In-Person' : 'Présentiel'}
                 </h4>
                 <p className="text-sm text-slate-600 dark:text-slate-400">
                   {currentLang === 'ar'
                     ? 'جلسات تفاعلية يؤطرها خبراؤنا المعتمدون في مقرات مؤسستكم أو في مراكز التدريب التابعة لنا. مثالي للورشات التطبيقية.'
                     : currentLang === 'en'
                     ? 'Interactive sessions led by our senior experts at your premises or our dedicated centers. Ideal for hands-on workshops.'
                     : 'Sessions interactives animées par nos experts dans vos locaux ou dans nos centres de formation dédiés. Idéal pour les ateliers pratiques.'}
                 </p>
               </div>
            </div>
            <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex gap-4 rounded-sm">
               <div className="mt-1">
                 <Monitor className="w-5 h-5 text-slate-700 dark:text-slate-300" />
               </div>
               <div>
                 <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                   {currentLang === 'ar' ? 'تدريب تفاعلي عن بُعد (مباشر)' : currentLang === 'en' ? 'Online Virtual Live' : 'En Ligne (Live)'}
                 </h4>
                 <p className="text-sm text-slate-600 dark:text-slate-400">
                   {currentLang === 'ar'
                     ? 'فصول افتراضية تفاعلية مباشرة تتيح التواصل المباشر مع المدربين وتوفر أعلى درجات المرونة والفعالية.'
                     : currentLang === 'en'
                     ? 'Live virtual classrooms enabling real-time interaction with senior instructors, offering peak flexibility and efficiency.'
                     : 'Classes virtuelles en direct permettant une interaction en temps réel avec les formateurs, offrant flexibilité et efficacité.'}
                 </p>
               </div>
            </div>
          </div>
        </div>

        {/* Domaines d'Expertise */}
        <div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
             {currentLang === 'ar' ? 'مجالات وتخصصات التدريب' : currentLang === 'en' ? 'Core Disciplines' : "Domaines d'Expertise"}
          </h3>
          <div className="flex flex-col gap-6">
            
            {/* Domain 1 */}
            <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-sm overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-900 p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
                    {currentLang === 'ar' ? 'تدريب' : currentLang === 'en' ? 'Training' : 'Formation'}
                  </span>
                  <ShieldCheck className="w-4 h-4 text-slate-400" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {currentLang === 'ar' ? 'التدقيق الداخلي والامتثال' : currentLang === 'en' ? 'Internal Audit & Compliance' : 'Audit Interne & Conformité'}
                </h4>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {currentLang === 'ar'
                    ? 'إتقان منهجيات التدقيق وضمان المطابقة التامة لأرقى المعايير الدولية.'
                    : currentLang === 'en'
                    ? 'Master rigorous audit methodologies and enforce strict adherence to international standards.'
                    : "Maîtrisez les méthodologies d'audit et assurez la conformité aux normes internationales."}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'تدقيق تكنولوجيا ونظم المعلومات' : currentLang === 'en' ? 'IT Audit & Information Systems' : "Audit IT et Systèmes d'Information"}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'التحضير لشهادات الاعتماد CISA / IRCA' : currentLang === 'en' ? 'CISA / IRCA Certification Preparation' : 'Préparation aux certifications CISA'}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'إدارة المخاطر المؤسسية (ISO 31000)' : currentLang === 'en' ? 'Enterprise Risk Management (ISO 31000)' : 'Gestion des risques (ISO 31000)'}
                  </li>
                </ul>
                <a 
                  href="#contact"
                  className="w-full py-3 border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 rounded-sm"
                >
                  {currentLang === 'ar' ? 'طلب البرنامج التفصيلي والتسجيل' : currentLang === 'en' ? 'View Syllabus & Inquire' : 'Voir le programme détaillé'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                </a>
              </div>
            </div>

            {/* Domain 2 */}
            <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-sm overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-900 p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
                    {currentLang === 'ar' ? 'تدريب' : currentLang === 'en' ? 'Training' : 'Formation'}
                  </span>
                  <CheckSquare className="w-4 h-4 text-slate-400" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {currentLang === 'ar' ? 'الجودة ومعايير الآيزو (ISO)' : currentLang === 'en' ? 'Quality & ISO Standards' : 'Qualité (ISO)'}
                </h4>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {currentLang === 'ar'
                    ? 'تطبيق واستدامة أنظمة إدارة جودة رائدة وعالية الكفاءة.'
                    : currentLang === 'en'
                    ? 'Implement and maintain state-of-the-art quality management systems.'
                    : 'Implémentez et maintenez des systèmes de management de la qualité performants.'}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'منفذ رئيسي معتمد ISO 9001 / ISO 27001' : currentLang === 'en' ? 'Lead Implementer ISO 9001 / ISO 27001' : 'Lead Implementer ISO 9001'}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'الترقية والتحول إلى الإصدارات الأحدث لمعايير ISO' : currentLang === 'en' ? 'ISO Transition & System Upgrades' : 'Transition et mise à niveau ISO'}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'التدقيق الداخلي للجودة والعمليات' : currentLang === 'en' ? 'Internal Quality & Process Auditing' : 'Audit Qualité Interne'}
                  </li>
                </ul>
                <a 
                  href="#contact"
                  className="w-full py-3 border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 rounded-sm"
                >
                  {currentLang === 'ar' ? 'طلب البرنامج التفصيلي والتسجيل' : currentLang === 'en' ? 'View Syllabus & Inquire' : 'Voir le programme détaillé'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                </a>
              </div>
            </div>

            {/* Domain 3 */}
            <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-sm overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-900 p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
                    {currentLang === 'ar' ? 'تدريب' : currentLang === 'en' ? 'Training' : 'Formation'}
                  </span>
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {currentLang === 'ar' ? 'الأداء والاستراتيجية المؤسسية' : currentLang === 'en' ? 'Performance & Strategic Governance' : 'Performance & Stratégie'}
                </h4>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {currentLang === 'ar'
                    ? 'تحسين العمليات التشغيلية ومواءمة استراتيجية تكنولوجيا المعلومات مع الأهداف المؤسسية.'
                    : currentLang === 'en'
                    ? 'Optimize operational workflows and align enterprise IT strategy with business goals.'
                    : "Optimisez vos processus et alignez votre stratégie IT avec vos objectifs d'affaires."}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'حوكمة تكنولوجيا المعلومات (COBIT & ITIL)' : currentLang === 'en' ? 'IT Governance (COBIT & ITIL)' : 'Gouvernance IT (COBIT)'}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'تحسين وإعادة هندسة العمليات التشغيلية' : currentLang === 'en' ? 'Business Process Optimization & Lean' : 'Optimisation des processus métier'}
                  </li>
                  <li className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                    {currentLang === 'ar' ? 'لوحات القيادة ومؤشرات الأداء الاستراتيجية' : currentLang === 'en' ? 'Strategic Dashboards & KPIs' : 'Tableaux de bord stratégiques'}
                  </li>
                </ul>
                <a 
                  href="#contact"
                  className="w-full py-3 border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 rounded-sm"
                >
                  {currentLang === 'ar' ? 'طلب البرنامج التفصيلي والتسجيل' : currentLang === 'en' ? 'View Syllabus & Inquire' : 'Voir le programme détaillé'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};
